import { YjsEditor } from '@tunasong/sync-lib'

/**
 * The Y.Doc map that records which agent proposals have been applied to the doc (GH #2476).
 *
 * A proposal's resolve PATCH can be lost, for example when the tab closes right after Accept. The proposal then
 * comes back pending, and accepting it again applied its change a second time: a section added twice. The marker is
 * stored in the target doc itself, next to the edit, so it travels wherever the edit goes (y-indexeddb, the relay,
 * the server merge). If the edit survived, so did the marker.
 *
 * Namespaced so it cannot collide with the document content (`content`, see `getSharedTypeV2`) or any other
 * top-level type. Values are the ISO time of the apply. Readers of the Slate tree never see it.
 */
export const appliedProposalsKey = 'tunasong:applied-proposals'

const appliedProposals = (editor: unknown) =>
  YjsEditor.isYjsEditor(editor) ? editor.sharedRoot.doc?.getMap<string>(appliedProposalsKey) : undefined

/** Whether the proposal has already been applied to this editor's doc. False without a Y.Doc. */
export const isProposalApplied = (editor: unknown, proposalId: string) =>
  appliedProposals(editor)?.has(proposalId) ?? false

/**
 * Record that the proposal has been applied to this editor's doc. Call it right after applying the change, which
 * must be synchronous (see `AgentCommandApplyFn`).
 *
 * The pending Slate ops are flushed and the marker set in one Yjs transaction, so they go out as one update: it can
 * never be persisted or relayed apart, leaving an edit without its marker (a double apply) or a marker without its
 * edit (the change blocked for good). slate-yjs's flush transacts on the same doc, and Yjs folds a nested
 * transaction into the outer one. It is written with the editor's local origin, so the websocket provider sends it
 * like any local edit. A no-op without a Y.Doc.
 */
export const markProposalApplied = (editor: unknown, proposalId: string) => {
  if (!YjsEditor.isYjsEditor(editor)) {
    return
  }
  const map = appliedProposals(editor)
  if (!map?.doc) {
    return
  }
  map.doc.transact(() => {
    YjsEditor.flushLocalChanges(editor)
    map.set(proposalId, new Date().toISOString())
  }, editor.localOrigin)
}
